import {Box, Checkbox, Radio, Typography} from '@mui/material';
import {alpha} from '@mui/material/styles';
import type {SxProps, Theme} from '@mui/material/styles';

import {greenColors, orangeColors} from '../../../../../../../layouts/theme/designSystem';
import {AssessmentQuestion} from '../../../../../../../types/shared-recruiting-types';

interface Props {
  questions?: AssessmentQuestion[];
}

/** Muted, static styling so disabled radios/checkboxes read as display-only, not form inputs. */
const readOnlyChoiceControlSx = (selected: boolean): SxProps<Theme> => ({
  padding: 0,
  pointerEvents: 'none',
  cursor: 'default',
  color: selected ? greenColors[70] : alpha(greenColors[100], 0.38),
  '&.Mui-checked': {
    color: greenColors[70],
  },
  '&.Mui-disabled': {
    color: selected ? greenColors[70] : alpha(greenColors[100], 0.38),
  },
  '&.Mui-checked.Mui-disabled': {
    color: greenColors[70],
    opacity: 0.92,
  },
});

export const AssessmentResults = ({questions}: Props) => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        mt: 2,
      }}
    >
      {questions?.map((question, questionIndex) => (
        <Box
          key={questionIndex}
          sx={{
            padding: 2,
          }}
        >
          <Typography variant='body1' fontWeight={700} sx={{mb: 1}}>
            {question.question}
          </Typography>
          <Box sx={{display: 'flex', flexDirection: 'column'}}>
            {question.answers.map((answer, answerIndex) => {
              if (typeof answer === 'string') {
                return (
                  <Box
                    key={answerIndex}
                    sx={{
                      padding: 1.5,
                      borderRadius: 0.5,
                      backgroundColor: orangeColors[5],
                    }}
                  >
                    <Typography variant='body2' sx={{whiteSpace: 'pre-wrap'}}>
                      {answer}
                    </Typography>
                  </Box>
                );
              }

              const selected = answer.selectedByApplicant;

              return (
                <Box
                  key={answerIndex}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    padding: 1,
                    borderRadius: 0.5,
                    cursor: 'default',
                    backgroundColor: selected ? orangeColors[5] : 'transparent',
                    border: `1px solid ${selected ? alpha(orangeColors[40], 0.45) : 'transparent'}`,
                  }}
                >
                  {question.type === 'single_choice' ? (
                    <Radio checked={selected} disabled tabIndex={-1} sx={readOnlyChoiceControlSx(selected)} />
                  ) : (
                    <Checkbox checked={selected} disabled tabIndex={-1} sx={readOnlyChoiceControlSx(selected)} />
                  )}
                  <Typography
                    variant='body2'
                    sx={{
                      color: selected ? greenColors[900] : alpha(greenColors[900], 0.55),
                      fontWeight: selected ? 600 : 400,
                    }}
                  >
                    {answer.text}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Box>
      ))}
    </Box>
  );
};
